// src/components/VersionHistory.tsx
//
// ★★★ WAVE 12 (P4) — ONE CODE'S PAST VERSIONS, AND THE BI-TEMPORAL REWIND, ON A
// SCREEN AT LAST.
//
// The RTM's Annex A 26–28 row carried this as a gap: *"Not built on the screens: a
// history/point-in-time (`?asRecordedAt=`) view of a past version — the API supports
// it, the screen shows only the current"*. `WardClassesPage`'s own header said the
// same thing about itself, honestly, for six phases.
//
// ══ ⚠ WHY THIS IS A SHARED COMPONENT AND NOT A BLOCK ON ONE PAGE ══
//
// **Thirteen** administered catalogues have the identical series route and the
// identical `?asRecordedAt=` parameter — `027_effective_dated_entity.sql` is the one
// trigger they all share. Building the control thirteen times is thirteen chances to
// word the rewind differently, and the WORDING is the hard part here: a reader who
// misunderstands what a point-in-time view is being told will misread a rate that
// moved money. So the explanation lives once.
//
// ══ ★★★ THE TWO SENTENCES THIS COMPONENT EXISTS TO GET RIGHT ══
//
// A bi-temporal rewind is **not** a date filter, and every screen that has ever
// shipped one has had to say so:
//
//   1. **A version recorded LATER is not shown.** That is the part readers expect.
//   2. **A window CLOSED later is handed back OPEN**, because at the instant asked
//      about, nobody had closed it yet. That is the part nobody expects, and it is
//      the whole reason the rewind is worth having — it reconstructs what the system
//      would have ANSWERED then, not what it now knows about then.
//
// ⚠ **Wave 11 is why this is buildable today.** S-078 found that twelve repositories
// compiled this rewind with an inclusive bound against a microsecond column, so
// asking for a version's OWN recorded instant returned an **empty history** — 0 rows
// where 1 was correct, measured on the live estate. A screen built before that fix
// would have rendered a confidently wrong reconstruction of the past, on the
// catalogues pro-ration reads from.
import { useState } from 'react'
import { SgdsButton, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { targetValue } from '../lib/sgds-dom'
import { ErrorCard } from './StateCards'
import { formatInstant, formatIsoDate } from '../lib/format'

/**
 * One version, reduced to what a history needs. Every catalogue's row satisfies
 * this — they all carry an effective window, a recorded instant and a note, from
 * the shared `027_effective_dated_entity.sql` shape.
 *
 * ⚠ A STRUCTURAL type rather than a union of the thirteen row types: this component
 * must not import thirteen shapes to render four fields, and a catalogue that grows
 * a fourteenth field should not have to touch this file.
 */
export interface VersionedEntry {
  /**
   * The window, for the twelve catalogues that call it `effective`.
   *
   * ⚠ **OPTIONAL, because one catalogue does not have it and that is deliberate on
   * its part.** `MbimRecord` carries `currency: { issuedOn, supersededOn }` — an
   * MBIM record is ISSUED and SUPERSEDED, not effective-from/until, and renaming
   * its fields to fit this component would be the tail wagging the domain. Such a
   * catalogue supplies {@link VersionHistoryProps.windowOf} instead.
   */
  readonly effective?: { readonly from: string; readonly until: string | null }
  readonly recordedAt: string
  readonly note: string | null
}

export interface VersionHistoryProps<T extends VersionedEntry> {
  /** What is being reconstructed — a code, a parameter, a key. Shown in the caption. */
  readonly subject: string
  readonly entries: readonly T[]
  readonly loading: boolean
  readonly error: string | null
  readonly onRetry: () => void
  /**
   * The instant currently being reconstructed, or `''` for "as it stands now".
   * Owned by the PAGE, not by this component, because it is a query parameter: the
   * page is what re-fetches.
   */
  readonly asRecordedAt: string
  readonly onAsRecordedAtChange: (value: string) => void
  /** Extra columns this catalogue wants beside the shared four. */
  readonly columns?: readonly { readonly header: string; readonly cell: (entry: T) => React.ReactNode }[]
  /**
   * ★★★ WAVE 12 (P4) — THE CODE PICKER, when the page has no other way in.
   *
   * `WardClassesPage` opens a history from a **History** button on each row, which
   * is the better affordance: the code is right there. The other administered
   * catalogues have tables of differing shapes, and adding a column to each meant
   * editing twelve `thead`/`tbody` pairs — twelve chances to break a working
   * screen for a control that can live in one place instead.
   *
   * ⚠ So this component offers its own picker when given the codes, and the row
   * buttons stay where they already work. Both set the same state; there is one
   * `<select>` in the codebase rather than twelve, which is the same S-011
   * reasoning that put the rewind's WORDING here.
   *
   * ⚠ Options are the codes **on the current page**, not the whole catalogue, and
   * `subjectHint` is where a page says so. A picker that silently offered only
   * what happened to load would be the same "sample presented as a census" defect
   * `AttentionPage` was fixed for in Wave 11.
   */
  readonly subjectOptions?: readonly string[]
  readonly onSubjectChange?: (subject: string) => void
  /** What the picker is choosing from, in the page's own words. */
  readonly subjectHint?: string
  /**
   * ★★★ WAVE 12 (P4) — where the window lives, for a catalogue that does not call
   * it `effective`.
   *
   * ⚠ `MbimRecord` is the case: `currency: { issuedOn, supersededOn }`, because an
   * MBIM record is ISSUED and SUPERSEDED. Its vocabulary is right for its domain
   * and this component does not get to rename it.
   *
   * ⚠ **Absent AND no `effective` on the row is rendered LOUDLY, not blank.** A
   * silent em-dash would make a catalogue that forgot this prop look like one whose
   * windows are genuinely unknown — the "not applicable versus not captured"
   * distinction this component already keeps for an open window.
   */
  readonly windowOf?: (entry: T) => { readonly from: string; readonly until: string | null }
  /** Column headers for the window, when this catalogue names it something else. */
  readonly windowHeaders?: { readonly from: string; readonly until: string }
}

export function VersionHistory<T extends VersionedEntry>({
  subject,
  entries,
  loading,
  error,
  onRetry,
  asRecordedAt,
  onAsRecordedAtChange,
  columns = [],
  subjectOptions,
  onSubjectChange,
  subjectHint,
  windowOf,
  windowHeaders,
}: VersionHistoryProps<T>) {
  // ⚠ Loud, not blank, when neither is available — see `windowOf`'s docstring.
  const readWindow = (entry: T): { from: string; until: string | null } =>
    windowOf !== undefined
      ? windowOf(entry)
      : (entry.effective ?? { from: 'window not supplied to this table', until: null })
  const fromHeader = windowHeaders?.from ?? 'Effective from'
  const untilHeader = windowHeaders?.until ?? 'Effective until'
  // ⚠ A DRAFT, applied on submit rather than on every keystroke. Each change is a
  // round trip to a bi-temporal query; re-fetching per character would issue a
  // request per digit of a typed instant, and every one of them writes an
  // `audit.event` row (reading a catalogue is audited like any other read).
  const [draft, setDraft] = useState(asRecordedAt)
  const rewound = asRecordedAt.length > 0

  const picker = subjectOptions !== undefined && onSubjectChange !== undefined
  const chosen = subject.length > 0

  return (
    <section className="pt-card">
      <h2 className="text-lg mb-1">Version history{chosen ? `: ${subject}` : ''}</h2>
      <p className="text-sm text-[var(--sgds-color-subtle)] mb-4 max-w-[75ch]">
        Every version ever recorded for a code, oldest first. Leave the instant empty to see the history as it stands
        now, or give one to reconstruct what this system would have answered at that moment.
      </p>

      {picker && (
        <div className="pt-controlbar mb-4">
          {/* ★ WAVE 26 — the house `SgdsSelect`, not a native `<select>`. The hint
              rides `hintText` and ⚠ says WHICH population the options come from:
              the codes on the page above, not the whole catalogue — offering a
              partial list without saying so is the "sample presented as a census"
              defect AttentionPage was fixed for in Wave 11. */}
          <div className="max-w-[60ch]">
            <SgdsSelect
              label="Code"
              value={subject}
              hintText={subjectHint ?? 'The codes listed above. Page or sort that table to reach others.'}
              onSgdsChange={(event) => onSubjectChange(targetValue(event))}
            >
              <SgdsSelectOption value="">Choose a code&hellip;</SgdsSelectOption>
              {subjectOptions.map((option) => (
                <SgdsSelectOption key={option} value={option}>
                  {option}
                </SgdsSelectOption>
              ))}
            </SgdsSelect>
          </div>
        </div>
      )}

      {/* ⚠ Nothing below is rendered until a code is chosen. A rewind control over
          no subject would invite an officer to set an instant and see nothing
          happen, and an empty table under it reads as "this code has no history". */}
      {!chosen ? (
        <p className="text-sm text-[var(--sgds-body-color-subtle)]">
          Choose a code to see every version ever recorded for it, and to reconstruct it as at any instant.
        </p>
      ) : (
      <>
      <form
        className="pt-controlbar mb-4"
        onSubmit={(event) => {
          event.preventDefault()
          onAsRecordedAtChange(draft)
        }}
      >
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-semibold">As the system knew it at</span>
          <input
            type="text"
            className="pt-input pt-mono"
            placeholder="2026-08-01T00:00:00.000Z"
            aria-describedby="pt-asrecordedat-hint"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <span id="pt-asrecordedat-hint" className="text-xs text-[var(--sgds-color-subtle)] max-w-[60ch]">
            A UTC instant. Empty means now. The server refuses anything it cannot read as an instant, rather than
            silently showing you the present.
          </span>
        </label>
        <div className="pt-controlbar-action flex gap-2">
          <SgdsButton variant="outline" size="sm" type="submit">
            Reconstruct
          </SgdsButton>
          {rewound && (
            <SgdsButton
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => {
                setDraft('')
                onAsRecordedAtChange('')
              }}
            >
              Back to now
            </SgdsButton>
          )}
        </div>
      </form>

      {/*
        ★★★ THE HONESTY BANNER, and it is the reason this component exists rather
        than a plain table. A rewound view looks EXACTLY like a current one — same
        columns, same rows, plausible dates — so nothing on screen would tell a
        reader they are looking at a reconstruction unless it is said. And the second
        sentence is the one nobody expects: a window closed after this instant is
        shown OPEN, because at this instant nobody had closed it.
      */}
      {rewound && (
        <div role="status" className="pt-well p-4 mb-3">
          <p className="font-semibold">
            Reconstructed as at <span className="pt-mono">{asRecordedAt}</span>
          </p>
          <p className="mt-2 text-sm text-[var(--sgds-body-color-subtle)]">
            This is not a filter on dates: it is what this system would have answered at that instant. A version
            recorded after it is not shown, and{' '}
            <strong>a window that was closed after it is shown as still open</strong>, because at that moment nobody
            had closed it yet. Amounts and rules read from a reconstruction must never be used to decide a bill
            today.
          </p>
        </div>
      )}

      {error !== null ? (
        <ErrorCard title="Could not load the version history" message={error} onRetry={onRetry} />
      ) : loading && entries.length === 0 ? (
        <p className="text-sm text-[var(--sgds-body-color-subtle)]" aria-busy="true">
          Loading the version history…
        </p>
      ) : entries.length === 0 ? (
        <div className="pt-well p-4">
          <p className="font-semibold">
            {rewound ? 'Nothing had been recorded for this code yet' : 'No versions recorded for this code'}
          </p>
          <p className="mt-2 text-sm text-[var(--sgds-body-color-subtle)]">
            {/* ⚠ Two different facts, and a shared sentence would make the more
                surprising one unreadable. An empty REWOUND history means the code
                did not exist yet at that instant — which is a real answer, not a
                missing one. */}
            {rewound
              ? 'This code had no version recorded at that instant. Move the instant later, or go back to now.'
              : 'Nothing is missing: no version has been recorded for this code yet.'}
          </p>
        </div>
      ) : (
        <div className="pt-table-wrap" aria-busy={loading}>
          <table className="pt-table">
            <caption className="sr-only">
              Every version of {subject}, oldest first
              {rewound ? `, as the system knew it at ${asRecordedAt}` : ''}
            </caption>
            <thead>
              <tr>
                {/*
                  ⚠ PLAIN `<th>`, not `SortableTh`, and that is deliberate against the
                  standing rule that every table header is sortable. The rule exists so
                  a reader can find a row; the READING ORDER of a history is its
                  meaning, and letting a reader put a supersession above the version it
                  superseded would make the table say something false. Stated here
                  because the next person to sweep for unsortable headers will find
                  this one.
                */}
                <th scope="col" data-unsortable="an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set, and a supersession above the version it superseded would make this table say something false">
                  {fromHeader}
                </th>
                <th scope="col" data-unsortable="an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set, and a supersession above the version it superseded would make this table say something false">
                  {untilHeader}
                </th>
                {columns.map((column) => (
                  <th scope="col" key={column.header} data-unsortable="an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set, and a supersession above the version it superseded would make this table say something false">
                    {column.header}
                  </th>
                ))}
                <th scope="col" data-unsortable="an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set, and a supersession above the version it superseded would make this table say something false">
                  Recorded at
                </th>
                <th scope="col" data-unsortable="an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set, and a supersession above the version it superseded would make this table say something false">
                  Note
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, index) => (
                // ⚠ The INDEX is part of the key, not the window: two versions can share
                // a from-date on a catalogue whose window is a pair of dates, and a
                // duplicate React key silently drops a row from a HISTORY.
                <tr key={`${index}-${entry.recordedAt}`}>
                  <td className="pt-tabular">{formatIsoDate(readWindow(entry).from)}</td>
                  <td className="pt-tabular">
                    {readWindow(entry).until === null ? (
                      // ⚠ Said in words, never left blank. Blank reads as "unknown";
                      // an open window is a definite fact, and under a rewind it is
                      // the fact most likely to be misread.
                      <span className="text-[var(--sgds-body-color-subtle)]">still in force</span>
                    ) : (
                      formatIsoDate(readWindow(entry).until as string)
                    )}
                  </td>
                  {columns.map((column) => (
                    <td key={column.header}>{column.cell(entry)}</td>
                  ))}
                  <td className="pt-tabular">{formatInstant(entry.recordedAt)}</td>
                  <td>{entry.note ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}
    </section>
  )
}
